<template>
  <div class="item">
    <div class="image">
      <div class="kind" :class="{ [item.dataType]: true }">{{ kind }}</div>
      <img class="image-item" :src="image" />
    </div>
    <div class="body">
      <div class="left">
        <div>
          <div class="theme-title">{{ $t("eventThemeItem.themeTitle") }}</div>
          <div class="theme" :style="{ color }">{{ theme }}</div>
          <div class="description">{{ description }}</div>
        </div>
        <div v-if="isContest">
          <div class="award">{{ award }}</div>
          <div class="deadline">{{ $t("eventThemeItem.deadlineLabel") }}{{ deadline }}</div>
        </div>
      </div>
      <div class="right">
        <div class="owner-container">
          <div class="owner-title">{{ $t("eventThemeItem.organizer") }}</div>
          <div class="owner">{{ owner }}</div>
        </div>
        <div class="button-container">
          <button class="button primary" @click="onClickPrimary">
            <div>{{ $t("eventThemeItem.viewDetailsButton") }}</div>
          </button>
          <button class="button secondary" @click="onClickSecondary">
            <div>{{ $t("eventThemeItem.writeSubmissionButton") }}</div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import { format } from "date-fns";
import { createUrlWithUtmParams, optimizeImage } from "@/lib/utils/url";
import { NolaItemId, NolaPageName } from "@/lib/utils/analytics";
import { getDateFnsLocale, DateFnsLocale } from "@/lib/utils/i18n/locale";

export default Vue.extend<Data, Methods, Computed, Props>({
  props: {
    item: Object as PropType<Item>,
  },
  computed: {
    dateFnsLocale() {
      return getDateFnsLocale(this.$i18n.locale);
    },
    isContest() {
      const { item } = this;
      return item.dataType === "contest";
    },
    kind() {
      const { isContest } = this;
      return isContest ? this.$t("eventThemeItem.kind.contest") : this.$t("eventThemeItem.kind.company");
    },
    image() {
      const { item, isContest } = this;
      const image = isContest ? item.image.url : item.bannerImg.url;
      return optimizeImage(`${image}?fit=crop&w=200&h=200`);
    },
    theme() {
      const { item } = this;
      // themeName があればそれを優先。novelGenre は未定義の可能性があるため安全に扱う
      if (item && item.themeName && String(item.themeName).length > 0) {
        return item.themeName;
      }
      const novelGenre = item && item.novelGenre ? String(item.novelGenre) : "";
      if (!novelGenre) return "";
      return novelGenre.replaceAll(" / ", "×");
    },
    description() {
      const { item, isContest } = this;
      return isContest ? item.free : item.comment;
    },
    award() {
      const { item } = this;
      return item.award;
    },
    deadline() {
      const { item, dateFnsLocale } = this;
      const formatStr = this.$t("common.deadlineFormat") as string;
      return format(item.deadline, formatStr, { locale: dateFnsLocale });
    },
    owner() {
      const { item, isContest } = this;
      return isContest ? item.organizer : item.name;
    },
    color() {
      const { item } = this;
      return item.color;
    },
  },
  methods: {
    onClickPrimary() {
      const { item, isContest } = this;
      if (isContest) this.$router.push({ name: "eventContestDetail", params: { id: item.id } });
      else
        window.open(
          createUrlWithUtmParams(
            `${process.env.VUE_APP_NOLANOVEL_WEB}/company/${item.id}`,
            NolaPageName.EventTheme,
            NolaItemId.CompanyCardViewDetailsButton
          ),
          process.env.VUE_APP_NOLANOVEL_NAME
        );
    },
    onClickSecondary() {
      this.$router.push({ name: "novelCreate" });
    },
  },
});

interface Props {
  item: Item;
}

interface Data {}

interface Computed {
  dateFnsLocale: DateFnsLocale;
  isContest: boolean;
  kind: string;
  image: string;
  theme: string;
  description: string;
  award: string;
  deadline: string;
  owner: string;
  color: string;
}

interface Methods {
  onClickPrimary(): void;
  onClickSecondary(): void;
}

interface Item {
  [key: string]: any;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.item {
  background-color: var(--color-surface-base);
  margin-bottom: 15px;
  border-radius: var(--radius-sm);
  display: flex;
  color: var(--color-text-normal);

  &:last-of-type {
    margin-bottom: 0;
  }

  .image {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;

    .kind {
      position: absolute;
      top: 0px;
      left: 0px;
      box-sizing: border-box;
      padding: 2px 8px;
      border-radius: var(--radius-sm) 0 var(--radius-sm) 0;
      font-weight: var(--font-weight-bold);
      font-size: 0.9em;
      color: var(--color-text-on-fixed-bg);

      &.contest {
        background-color: var(--color-brand-contest);
        border: 1px solid var(--color-brand-contest);
      }

      &.company {
        background-color: var(--color-brand-company);
        border: 1px solid var(--color-brand-company);
      }
    }

    .image-item {
      border-radius: var(--radius-sm) 0 0 var(--radius-sm);
    }
  }

  .body {
    flex: 1;
    display: flex;
    box-sizing: border-box;
    padding: 20px 30px;

    .left {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      margin-right: 30px;

      .theme-title {
        font-weight: var(--font-weight-bold);
        color: var(--color-text-low);
      }

      .theme {
        font-weight: var(--font-weight-bold);
        font-size: 1.5em;
        margin: 0.5em 0;
      }

      .description {
        font-weight: var(--font-weight-bold);
        font-size: 1.1em;
        color: var(--color-text-normal);
      }

      .award {
        font-weight: var(--font-weight-bold);
        color: var(--color-text-low);
        margin-bottom: 0.5em;
      }

      .deadline {
        font-weight: var(--font-weight-bold);
        color: var(--color-text-low);
      }
    }

    .right {
      width: 200px;
      box-sizing: border-box;
      padding-left: 30px;
      border-left: 3px solid var(--color-border);
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      .owner-container {
        .owner-title {
          font-weight: var(--font-weight-bold);
          color: var(--color-text-low);
        }

        .owner {
          font-weight: var(--font-weight-bold);
          font-size: 1.1em;
          margin-top: 0.5em;
          color: var(--color-text-normal);
        }
      }

      .button-container {
        display: flex;
        flex-direction: column;

        .button {
          position: relative;
          @include hover-overlay;
          display: flex;
          justify-content: center;
          min-width: max-content;
          margin: 0;
          margin-bottom: 10px;
          padding: 10px;
          border-radius: var(--radius-sm);
          font-weight: var(--font-weight-bold);
          cursor: pointer;

          &:last-of-type {
            margin-bottom: 0;
          }


          &.primary {
            color: var(--color-text-on-fixed-bg);
            background-color: var(--color-primary);
            border-color: var(--color-primary);
          }

          &.secondary {
            color: var(--color-primary);
            background-color: var(--color-background-normal);
            border-color: var(--color-primary);
          }
        }
      }
    }
  }
}
</style>
